import type { QueryClient } from '@tanstack/react-query'
import type { ErrorComponentProps } from '@tanstack/react-router'
import { Link, Outlet, createRootRouteWithContext } from '@tanstack/react-router'
import { MotionConfig } from 'motion/react'
import { Suspense, lazy } from 'react'

import '@/styles/tokens.css'
import '@/styles/global.css'

export interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RootError,
  notFoundComponent: RootNotFound,
})

function RootLayout() {
  return (
    // Every spatial transition in the app collapses to a fast crossfade for
    // anyone who asked their OS for less motion. Set once, here, so no
    // individual animation can forget.
    <MotionConfig reducedMotion="user">
      <Outlet />
      <Devtools />
    </MotionConfig>
  )
}

/**
 * The last resort.
 *
 * A render that threw, or a loader that could not reach the server at all —
 * which for a self-hosted app is a real and ordinary state, not a bug: the
 * container is restarting, or the machine in the cupboard is rebooting.
 *
 * So it says what is probably true rather than showing a stack trace, and
 * offers the two things that actually help.
 */
function RootError({ error, reset }: ErrorComponentProps) {
  const offline = typeof navigator !== 'undefined' && !navigator.onLine

  return (
    <main className="pane-column" role="alert">
      <h1 className="pane-title">{offline ? 'You are offline' : 'nook cannot be reached'}</h1>
      <p className="muted">
        {offline
          ? 'Your device says it has no connection. This page will work again when it does.'
          : 'The server did not answer. If you host this instance yourself, it may be restarting.'}
      </p>
      <div className="error-actions">
        <button type="button" className="btn btn-primary" onClick={reset}>
          Try again
        </button>
        <Link to="/" className="btn btn-secondary">
          Back to your lounges
        </Link>
      </div>
      {import.meta.env.DEV && <pre className="error-detail">{String(error)}</pre>}
    </main>
  )
}

function RootNotFound() {
  return (
    <main className="pane-column">
      <h1 className="pane-title">Nothing here</h1>
      <p className="muted">
        That address does not lead anywhere in this instance. It may have been a room that has since
        been deleted.
      </p>
      <p>
        <Link to="/">Back to your lounges</Link>
      </p>
    </main>
  )
}

// Off unless asked for. They are genuinely useful and they also park two
// floating buttons on top of the composer and the voice dock, which is exactly
// where the interesting UI is — so opting in beats dismissing them every time:
//
//   VITE_DEVTOOLS=1 just dev        once
//   echo 'VITE_DEVTOOLS=1' >> web/.env.local   for good (gitignored)
//
// Still dev-only and still dynamically imported, so the production bundle never
// carries them either way.
const Devtools =
  import.meta.env.DEV && import.meta.env.VITE_DEVTOOLS === '1' ? DevtoolsPanels : () => null

const RouterDevtools = lazy(() =>
  import('@tanstack/react-router-devtools').then((m) => ({ default: m.TanStackRouterDevtools })),
)
const QueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })),
)

function DevtoolsPanels() {
  return (
    <Suspense fallback={null}>
      <RouterDevtools position="bottom-right" />
      <QueryDevtools buttonPosition="bottom-left" />
    </Suspense>
  )
}
